import { theme } from "antd";
import CloseIcon from "components/svg-icons/close-icon";
import { ProjectContext } from "context/ProjectProvider";
import { useContext, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * Some layouts (e.g. the dashboard timeline) are `position: fixed` with a
 * hard-coded top offset, so they read this variable to clear the banner.
 */
const BANNER_HEIGHT_CSS_VAR = "--ciq-archived-banner-height";

/** Banner height for viewport-based `calc()` heights; `0px` when no banner. */
export const ARCHIVED_BANNER_HEIGHT = `var(${BANNER_HEIGHT_CSS_VAR}, 0px)`;

function BannerBar({ onDismiss }: { onDismiss: () => void }) {
  const { token } = theme.useToken();
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const root = document.documentElement;
    const measure = () =>
      root.style.setProperty(
        BANNER_HEIGHT_CSS_VAR,
        `${ref.current?.offsetHeight ?? 0}px`
      );
    measure();
    const observer =
      typeof ResizeObserver === "undefined" || !ref.current
        ? null
        : new ResizeObserver(measure);
    if (observer && ref.current) observer.observe(ref.current);
    return () => {
      observer?.disconnect();
      root.style.removeProperty(BANNER_HEIGHT_CSS_VAR);
    };
  }, []);

  return (
    <div
      ref={ref}
      role="status"
      className="relative flex shrink-0 items-center justify-center px-12 py-2 font-normal tracking-normal"
      style={{
        background: "var(--ciq-notice-banner-bg)",
        color: "var(--ciq-black-a88)",
        fontFamily: "Inter, sans-serif",
        fontSize: token.fontSize,
        lineHeight: token.lineHeight
      }}
      data-testid="archived-project-banner"
    >
      This project is Archived. Restore to make any changes.
      <button
        type="button"
        className="absolute right-4 top-1/2 flex -translate-y-1/2 cursor-pointer items-center border-0 bg-transparent p-0"
        style={{ color: "var(--ciq-notice-banner-icon)" }}
        onClick={onDismiss}
        aria-label="Dismiss archived project banner"
        data-testid="archived-project-banner-dismiss"
      >
        <CloseIcon />
      </button>
    </div>
  );
}

/**
 * Rendered once by the project layout, directly below the navbar. Dismissing
 * hides it only for the current page; it shows again on the next page.
 */
export default function ArchivedProjectBanner() {
  const { isProjectArchived } = useContext(ProjectContext);
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(false);
  const [lastPathname, setLastPathname] = useState(pathname);

  if (lastPathname !== pathname) {
    setLastPathname(pathname);
    setDismissed(false);
  }

  if (!isProjectArchived || dismissed) return null;
  return <BannerBar onDismiss={() => setDismissed(true)} />;
}
